'use client'

import { useState, useEffect } from 'react'
import styles from './BackendSelector.module.css'

const STORAGE_KEY = 'customBackendUrl'

// Helper functions for localStorage management (for client-side display)
function getStoredUrl(): string | null {
  if (typeof window === 'undefined') return null
  return localStorage.getItem(STORAGE_KEY)
}

function setStoredUrl(url: string): void {
  if (typeof window !== 'undefined') {
    localStorage.setItem(STORAGE_KEY, url)
  }
}

function clearStoredUrl(): void {
  if (typeof window !== 'undefined') {
    localStorage.removeItem(STORAGE_KEY)
  }
}

export default function BackendSelector() {
  const [backendUrl, setBackendUrl] = useState('')
  const [currentUrl, setCurrentUrl] = useState('')
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    const savedUrl = getStoredUrl()
    if (savedUrl) {
      setBackendUrl(savedUrl)
    }
    setMounted(true)
  }, [])

  useEffect(() => {
    // Update current URL when component mounts or when localStorage changes
    const updateCurrentUrl = () => {
      const savedUrl = getStoredUrl()
      setCurrentUrl(savedUrl || process.env.NEXT_PUBLIC_BACKEND_ENDPOINT || '')
    }

    updateCurrentUrl()

    // Listen for localStorage changes (in case they change externally)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) {
        updateCurrentUrl()
      }
    }

    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [])

  const handleSave = async () => {
    if (backendUrl.trim()) {
      // Update localStorage for client-side display
      setStoredUrl(backendUrl.trim())

      // Also update the HTTP-only cookie via API for server-side usage
      try {
        await fetch('/api/set-backend-cookie', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ url: backendUrl.trim() }),
        })
      } catch (error) {
        console.warn('Failed to sync cookie to server:', error)
      }

      // Refresh the page automatically to apply changes
      window.location.reload()
    }
  }

  const handleReset = async () => {
    // Clear localStorage for client-side display
    clearStoredUrl()
    setBackendUrl('')

    // Also clear the HTTP-only cookie via API for server-side usage
    try {
      await fetch('/api/clear-backend-cookie', {
        method: 'POST',
      })
    } catch (error) {
      console.warn('Failed to clear cookie on server:', error)
    }

    // Refresh the page automatically to apply changes
    window.location.reload()
  }

  if (!mounted) return null

  return (
    <div className={styles.backendSelectorWrapper}>
      <h3 className={styles.title}>Backend Selector</h3>

      <div className={styles.urlDisplay}>
        <div className={styles.urlRow}>
          <label className={styles.label}>
            URL Original:
            <span className={styles.originalValue}>{process.env.NEXT_PUBLIC_BACKEND_ENDPOINT || 'No definida'}</span>
          </label>
        </div>
        <div className={styles.urlRow}>
          <label className={styles.label}>
            URL Actual:
            <span className={styles.currentValue}>{currentUrl}</span>
          </label>
        </div>
      </div>

      <div className={styles.inputGroup}>
        <label className={styles.label}>
          Nueva URL del Backend:
          <input
            type="url"
            value={backendUrl}
            onChange={(e) => setBackendUrl(e.target.value)}
            placeholder="https://tu-backend.com/"
            className={styles.urlInput}
          />
        </label>
      </div>

      <div className={styles.buttonGroup}>
        <button
          onClick={handleSave}
          disabled={!backendUrl.trim()}
          className={styles.saveButton}
        >
          Guardar
        </button>
        <button
          onClick={handleReset}
          className={styles.resetButton}
        >
          Restablecer
        </button>
      </div>

      <div className={styles.info}>
        <p className={styles.infoText}>
          ⚠️ Cambiar la URL del backend puede afectar la funcionalidad de la aplicación y provocará que tu sesión actual se cierre.
          Asegúrate de que la URL sea correcta antes de guardar.
        </p>
      </div>
    </div>
  )
}